/**
 * Proxy Warning Card (DC-724)
 *
 * Card component displaying a detected proxy feature warning.
 * Shows feature name, what it proxies for, risk level, confidence,
 * detection method, and recommendation.
 */

import React from 'react';
import './contextual-validity.css';

// ============================================================================
// TYPES
// ============================================================================

interface ProxyDetection {
  feature: string;
  proxies_for: string[];
  confidence: number;
  risk_level: 'low' | 'moderate' | 'high' | 'critical';
  detection_method: 'pattern' | 'correlation' | 'mutual_info';
  recommendation: string;
}

interface ProxyWarningCardProps {
  proxy: ProxyDetection;
  onDismiss?: () => void;
  expanded?: boolean;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get risk icon as Unicode character (safe - no XSS risk)
 */
const getRiskIcon = (level: string): string => {
  switch (level) {
    case 'critical': return '\u26A0'; // Warning triangle
    case 'high': return '\u26A0';
    case 'moderate': return '\u{1F7E1}'; // Yellow circle
    case 'low': return '\u{1F7E2}'; // Green circle
    default: return '\u2022'; // Bullet
  }
};

const getDetectionMethodLabel = (method: string): string => {
  switch (method) {
    case 'pattern': return 'Pattern Match';
    case 'correlation': return 'Statistical Correlation';
    case 'mutual_info': return 'Mutual Information';
    default: return method;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ProxyWarningCard: React.FC<ProxyWarningCardProps> = ({
  proxy,
  onDismiss,
  expanded = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(expanded);

  const confidencePercent = Math.round(proxy.confidence * 100);

  return (
    <div className={`proxy-warning-card risk-${proxy.risk_level}`}>
      <div className="proxy-card-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="proxy-feature-info">
          <span className={`risk-indicator risk-${proxy.risk_level}`}>
            {getRiskIcon(proxy.risk_level)}
          </span>
          <span className="proxy-feature-name">{proxy.feature}</span>
        </div>
        <div className="proxy-badges">
          <span className={`risk-badge risk-${proxy.risk_level}`}>
            {proxy.risk_level.toUpperCase()}
          </span>
          <span className="confidence-badge">
            {confidencePercent}%
          </span>
          <span className="expand-icon">{isExpanded ? '&#9650;' : '&#9660;'}</span>
        </div>
      </div>

      {isExpanded && (
        <div className="proxy-card-body">
          {/* Proxies For */}
          <div className="proxy-detail-row">
            <span className="detail-label">Proxies for:</span>
            <div className="protected-tags">
              {proxy.proxies_for.map((attr, idx) => (
                <span key={idx} className="protected-tag">
                  {attr.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>

          {/* Detection Method */}
          <div className="proxy-detail-row">
            <span className="detail-label">Detection:</span>
            <span className="detection-method">
              {getDetectionMethodLabel(proxy.detection_method)}
            </span>
          </div>

          {/* Confidence Bar */}
          <div className="proxy-detail-row">
            <span className="detail-label">Confidence:</span>
            <div className="confidence-bar-container">
              <div className="confidence-bar">
                <div
                  className={`confidence-fill risk-${proxy.risk_level}`}
                  style={{ width: `${confidencePercent}%` }}
                />
              </div>
              <span className="confidence-value">{confidencePercent}%</span>
            </div>
          </div>

          {/* Recommendation */}
          <div className="proxy-recommendation">
            <span className="recommendation-icon">&#128161;</span>
            <p>{proxy.recommendation}</p>
          </div>

          {onDismiss && (
            <div className="proxy-actions">
              <button className="btn-link" onClick={onDismiss}>
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProxyWarningCard;
